import ExcelJS from "exceljs";
import * as FileSaver from "file-saver";
import { storeResultantBom } from "../../../actions";
import {
  buildAssemblySpecs,
  formatMoneyAmount,
  FREIGHT_TARIFF_FOOTER_ARGB,
  getExportManufacturer,
  getExportManufacturerPartNumber,
  getExportManufacturerPartNumberCellValue,
  countUniqueQuotedVendors,
  collectUniqueQuotedVendors,
  DIGIKEY_REFRESH_DETAILS_COLUMN,
  getTableColumnKeys,
  hasLineAlternateSelected,
  isBomSectionRow,
  isBomTotalRow,
  isExportManufacturerColumn,
  isExportMpnColumn,
  isLeadlessLine,
  isObsoleteLine,
  isUnmatchedLine,
  LEADLESS_HIGHLIGHT_ARGB,
  OBSOLETE_HIGHLIGHT_ARGB,
  prepareBomlinesForExport,
  UNMATCHED_HIGHLIGHT_ARGB,
} from "../../../constants/digiKeyColumns";
import { normalizeIssueReport } from "../../../constants/issueReportUtils";
import { isRealBomLine } from "../../../constants/quoteQuantities";
import { addIssueReportWorksheet } from "../../../utils/issueReportExport";

const addATemplateRowWithBasicCellFormat = (sheet, titleData) => {
  let columnsCount = Object.keys(titleData).length;
  let rowCellsStrings = Array(columnsCount).join(".").split(".");
  let rowTemplate = sheet.addRow(rowCellsStrings);
  rowTemplate._cells.forEach((cell, index) => {
    cell.border = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };
  });
  return rowTemplate;
};

const addSheetTitleRowAndGetInputColumnsCount = (
  rowTemplate,
  titleData,
  columnNames,
) => {
  let inputColumnsCount = 0;
  let titleRow = Object.keys(titleData);
  rowTemplate._cells.forEach((cell, index) => {
    let argb = "6c757d";
    let cellValue = columnNames.find(
      (item) => item.code_name === titleRow[index],
    );

    if (!cellValue) {
      cellValue = { name: titleRow[index] };
      argb = titleRow[index].indexOf("Quote ") !== -1 ? "0080ff" : "04b78a";
    } else {
      inputColumnsCount++;
    }

    cell.value = cellValue.name;
    cell.font = {
      color: { argb: "FFFFFFFF" },
      bold: true,
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: argb },
    };
  });

  return inputColumnsCount;
};

const isQuantityCellWithInvalidValue = (
  cellColumnTitle,
  invalidQuantityColumns,
  index,
) => {
  return (
    cellColumnTitle === "quantity" && invalidQuantityColumns.includes(index + 1)
  );
};

const getRichDescriptionCellValue = (descriptionObject) => {
  let descriptionCellRichTextArray = [];

  // eslint-disable-next-line array-callback-return
  descriptionObject.description.map(function (t) {
    if (t.key) {
      descriptionCellRichTextArray.push({
        font: {
          color: { argb: "636e95" },
        },
        text: t.key + ": ",
      });

      let value = t.link ? t.link : t.value ? t.value : "n/a";
      descriptionCellRichTextArray.push({
        text: value + " \r\n",
      });
    }
  });
  return {
    richText: descriptionCellRichTextArray,
  };
};

const parseExcelMoneyValue = (cellValue) => {
  if (cellValue == null || cellValue === "") {
    return 0;
  }
  if (typeof cellValue === "number") {
    return Number.isFinite(cellValue) ? cellValue : 0;
  }
  let text = "";
  if (typeof cellValue === "object" && Array.isArray(cellValue.richText)) {
    text = cellValue.richText.map((part) => part.text || "").join("");
  } else {
    text = String(cellValue);
  }
  const normalized = text.replace(/,/g, "").trim();
  if (!normalized || /^n\/a$/i.test(normalized)) {
    return 0;
  }
  const amount = parseFloat(normalized.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const excelColumnLetter = (columnNumber) => {
  let n = Number(columnNumber);
  let letters = "";
  while (n > 0) {
    const remainder = (n - 1) % 26;
    letters = String.fromCharCode(65 + remainder) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
};

const formatExportNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return "0";
  }
  return Number.isInteger(n) ? String(n) : String(n);
};

const thinBorder = {
  top: { style: "thin" },
  bottom: { style: "thin" },
  left: { style: "thin" },
  right: { style: "thin" },
};

const getRichResultCellValue = (resultObject) => {
  let resultCellRichTextArray = [];

  if (resultObject.title) {
    resultCellRichTextArray.push({
      font: {
        color: { argb: "4b499f" },
      },
      text: resultObject.title + " \r\n",
    });
  }

  if (resultObject?.text?.length > 0) {
    // eslint-disable-next-line array-callback-return
    resultObject.text.map(function (t) {
      if (t.text) {
        resultCellRichTextArray.push({
          font: {
            italic: t.class === "tdItalicSubTitle",
          },
          text: t.text,
        });
      }
    });
  }
  return {
    richText: resultCellRichTextArray,
  };
};

const getColorForResultCell = (resultObject) => {
  switch (resultObject.class) {
    case "bg-green":
      return "effbf8";
    case "bg-yellow":
      return "ffedad";
    case "bg-light-maroon":
      return "fdeeee";
    case "bg-gray-opacity":
      return "e9e9fc";
    default:
      return "ffffff";
  }
};

const addSheetDataRow = (
  rowTemplate,
  titleData,
  item,
  invalidQuantityColumns,
  rowIndex,
  sheet,
  lastBomLineIndex,
  bomChecklists,
) => {
  if (isBomSectionRow(item)) {
    const title =
      item._section_title ||
      Object.values(item).find(
        (value) => typeof value === "string" && value.trim() !== "",
      ) ||
      "";
    rowTemplate._cells.forEach((cell, index) => {
      cell.value = index === 0 ? title : "";
      cell.font = {
        color: { argb: "FFFFFFFF" },
        bold: true,
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "6c757d" },
      };
    });
    return;
  }

  let titleRow = Object.keys(titleData);
  const leadlessRow = isLeadlessLine(item);
  const obsoleteRow = isObsoleteLine(item);
  const unmatchedRow = isUnmatchedLine(item);
  rowTemplate._cells.forEach((cell, index) => {
    const columnKey = titleRow[index];
    let cellValue = item[columnKey];

    if (isExportMpnColumn(columnKey)) {
      const richMpn = getExportManufacturerPartNumberCellValue(item);
      cellValue = richMpn || getExportManufacturerPartNumber(item);
    } else if (isExportManufacturerColumn(columnKey)) {
      cellValue = getExportManufacturer(item);
    }

    let argb = isQuantityCellWithInvalidValue(
      columnKey,
      invalidQuantityColumns,
      rowIndex,
    )
      ? "fdeeee"
      : obsoleteRow
        ? OBSOLETE_HIGHLIGHT_ARGB
        : unmatchedRow
          ? UNMATCHED_HIGHLIGHT_ARGB
          : leadlessRow
            ? LEADLESS_HIGHLIGHT_ARGB
            : "ffffff";

    if (
      cellValue != null &&
      typeof cellValue === "object" &&
      cellValue.description != null
    ) {
      cellValue = getRichDescriptionCellValue(cellValue);
    } else if (
      cellValue != null &&
      typeof cellValue === "object" &&
      cellValue.richText
    ) {
      // Already formatted (e.g. obsolete alternate MPN cell).
    } else if (cellValue != null && typeof cellValue === "object") {
      argb = getColorForResultCell(cellValue);
      // Keep leadless/obsolete tint when quote/result cell has no status fill
      if (argb === "ffffff" && obsoleteRow) {
        argb = OBSOLETE_HIGHLIGHT_ARGB;
      } else if (argb === "ffffff" && unmatchedRow) {
        argb = UNMATCHED_HIGHLIGHT_ARGB;
      } else if (argb === "ffffff" && leadlessRow) {
        argb = LEADLESS_HIGHLIGHT_ARGB;
      }
      cellValue = getRichResultCellValue(cellValue);
    }
    const regexTest = /^BOM Manager Result \d+$/;
    if (
      cellValue &&
      regexTest.test(columnKey) &&
      cellValue?.richText?.length === 1
    ) {
      if (cellValue?.richText[0].text === "Multiple Manufacturers") {
        cellValue = {
          richText: [
            {
              text: cellValue?.richText[0].text,
              font: {
                color: {
                  argb: "ff0000",
                  theme: 1,
                },
                bold: true,
              },
            },
          ],
        };
      }
    }
    if (
      cellValue &&
      typeof cellValue !== "object" &&
      (isExportMpnColumn(columnKey) || columnKey === "designator") &&
      item["designator_color"] &&
      !hasLineAlternateSelected(item)
    ) {
      cellValue = {
        richText: [
          {
            text: cellValue,
            font: {
              color: {
                argb: "a02b93",
                theme: 1,
              },
              bold: true,
            },
          },
        ],
      };
    }
    cell.value = cellValue;
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: {
        argb: argb,
      },
    };
    cell.alignment = { wrapText: false };

    const regex = /^Quote \d+$/;
    if (regex.test(columnKey) && (isRealBomLine(item) || isBomTotalRow(item))) {
      const quoteAmount = parseExcelMoneyValue(cellValue);
      const quoteCell = sheet.getCell(cell?._address);
      quoteCell.numFmt = "$###0.00";
      quoteCell.value = quoteAmount;
    }

    if (
      /^Tariff Charge \d+$/.test(columnKey) &&
      (isRealBomLine(item) || isBomTotalRow(item))
    ) {
      const tariffCell = sheet.getCell(cell?._address);
      tariffCell.numFmt = "$###0.00";
      tariffCell.value = parseExcelMoneyValue(cellValue);
    }

    if (columnKey === "Quote 1" && rowIndex === lastBomLineIndex) {
      let listIndexNumber = 0;
      bomChecklists.forEach((checklist, listIndex) => {
        if (checklist?.active === 1) {
          let number = lastBomLineIndex + 4 + listIndexNumber;
          const cell2 = sheet.getCell("B" + number);
          cell2.value = checklist?.name;

          const cell1 = sheet.getCell("A" + number);
          cell1.dataValidation = {
            type: "list",
            allowBlank: true,
            formulae: ['"SELECT,TRUE,FALSE"'],
            showErrorMessage: true,
            errorTitle: "Invalid selection",
            error: "Please select a value from the dropdown list.",
          };
          cell1.value = "SELECT";

          listIndexNumber++;
        }
      });
    }
  });
};

const styleBorderedCell = (
  cell,
  { bold = false, numFmt = null, align = "left" } = {},
) => {
  cell.border = thinBorder;
  cell.font = { bold };
  cell.alignment = { horizontal: align, vertical: "middle" };
  if (numFmt) {
    cell.numFmt = numFmt;
  }
};

const getLastQuoteSumExcelRow = (bomlines) => {
  let lastIndex = (bomlines || []).length - 1;
  while (
    lastIndex >= 0 &&
    (isBomTotalRow(bomlines[lastIndex]) || isBomSectionRow(bomlines[lastIndex]))
  ) {
    lastIndex -= 1;
  }
  if (lastIndex < 0) {
    return 2;
  }
  return lastIndex + 2;
};

const addLegacyFreightTariffFooterRows = (
  sheet,
  columnCount,
  { freightTotal = 0, tariffTotal = 0, currency = "$" } = {},
) => {
  const colCount = Math.max(1, columnCount || 1);

  const addFooterRow = (label) => {
    const values = Array(colCount).fill("");
    values[0] = label;
    const row = sheet.addRow(values);
    row._cells.forEach((cell, index) => {
      cell.border = thinBorder;
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: FREIGHT_TARIFF_FOOTER_ARGB },
      };
      if (index === 0) {
        cell.font = { bold: true };
      } else {
        cell.value = "";
      }
    });
    if (colCount > 1) {
      sheet.mergeCells(row.number, 1, row.number, colCount);
    }
  };

  addFooterRow(
    `Freight: ${currency}${Number.isFinite(Number(freightTotal)) ? Number(freightTotal) : 0}`,
  );

  const tariffValue = Number(tariffTotal);
  if (Number.isFinite(tariffValue) && tariffValue > 0) {
    addFooterRow(`Tariff Charge : ${formatMoneyAmount(tariffValue, currency)}`);
  }
};

const headerFill = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "d9d9d9" },
};

const addAssemblySpecsTable = (sheet, bomlines, columnKeys, startRow) => {
  const lineSpecs = buildAssemblySpecs(bomlines, "lines");
  const qtyByLabel = new Map(buildAssemblySpecs(bomlines, "qty"));
  const firstQuoteCol =
    columnKeys.findIndex(
      (key) =>
        /^QuoteQty \d+$/.test(key) ||
        /^BOM Manager Result \d+$/.test(key) ||
        /^Quote \d+$/.test(key),
    ) + 1;
  let labelCol = 6;
  if (firstQuoteCol > 0 && labelCol + 2 >= firstQuoteCol) {
    labelCol = 3;
  }
  if (firstQuoteCol > 0 && labelCol + 2 >= firstQuoteCol) {
    labelCol = 1;
  }
  const linesCol = labelCol + 1;
  const qtyCol = labelCol + 2;

  const headerLabel = sheet.getCell(startRow, labelCol);
  const headerLines = sheet.getCell(startRow, linesCol);
  const headerQty = sheet.getCell(startRow, qtyCol);
  headerLabel.value = "Assembly Specs";
  headerLines.value = "Lines";
  headerQty.value = "Qty";
  [headerLabel, headerLines, headerQty].forEach((cell) => {
    cell.fill = headerFill;
    styleBorderedCell(cell, { bold: true, align: "center" });
  });

  lineSpecs.forEach((spec, index) => {
    const rowNumber = startRow + 1 + index;
    const labelCell = sheet.getCell(rowNumber, labelCol);
    const linesCell = sheet.getCell(rowNumber, linesCol);
    const qtyCell = sheet.getCell(rowNumber, qtyCol);
    labelCell.value = spec[0];
    linesCell.value = spec[1];
    qtyCell.value = qtyByLabel.has(spec[0]) ? qtyByLabel.get(spec[0]) : 0;
    styleBorderedCell(labelCell, { bold: true });
    styleBorderedCell(linesCell, { align: "right" });
    styleBorderedCell(qtyCell, { align: "right" });
  });

  const labelColumn = sheet.getColumn(labelCol);
  if (!labelColumn.width || labelColumn.width < 22) {
    labelColumn.width = 22;
  }
  [linesCol, qtyCol].forEach((col) => {
    const column = sheet.getColumn(col);
    if (!column.width || column.width < 10) {
      column.width = 10;
    }
  });

  return startRow + lineSpecs.length;
};

const cellHeaderText = (value) => {
  if (value == null || value === "") {
    return "";
  }
  if (typeof value === "object" && Array.isArray(value.richText)) {
    return value.richText
      .map((part) => part?.text || "")
      .join("")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  }
  return String(value)
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
};

const isCountryOfOriginHeader = (value) => {
  const text = cellHeaderText(value);
  return (
    text === "country of origin" ||
    text === "country_of_origin" ||
    text === DIGIKEY_REFRESH_DETAILS_COLUMN.toLowerCase()
  );
};

const findCountryOfOriginColumn = (sheet, columnKeys) => {
  const keys = columnKeys || [];
  const colCount = Math.max(keys.length, sheet.columnCount || 0, 1);

  for (let col = 1; col <= colCount; col += 1) {
    if (isCountryOfOriginHeader(sheet.getRow(1).getCell(col).value)) {
      return col;
    }
  }

  const originKeyIndex = keys.findIndex((key) => isCountryOfOriginHeader(key));
  if (originKeyIndex >= 0) {
    return originKeyIndex + 1;
  }

  const firstQuoteIndex = keys.findIndex(
    (key) =>
      /^QuoteQty \d+$/.test(key) ||
      /^BOM Manager Result \d+$/.test(key) ||
      /^Quote \d+$/.test(key),
  );
  // Quote groups sit immediately after Country of Origin, so the quote
  // index equals the 1-based Excel column of that origin column.
  if (firstQuoteIndex > 0) {
    return firstQuoteIndex;
  }

  return 0;
};

const resolveUniqueVendorNames = (bomlines, totals = {}) => {
  const seen = new Set();
  const addName = (name) => {
    const text = String(name || "")
      .replace(/^ORIGINAL_/i, "")
      .trim();
    if (text) {
      seen.add(text);
    }
  };

  collectUniqueQuotedVendors(bomlines).forEach(addName);
  for (let slot = 1; slot <= 10; slot += 1) {
    collectUniqueQuotedVendors(bomlines, slot).forEach(addName);
  }

  const nested = [
    totals.vendorNames,
    totals.vendors_name,
    totals.freightDetails?.vendors_name,
    totals.freight_details?.vendors_name,
  ];
  nested.forEach((list) => {
    if (Array.isArray(list)) {
      list.forEach(addName);
    }
  });

  return [...seen];
};

const addUniqueVendorNamesBelowCountryOfOrigin = (
  sheet,
  columnKeys,
  startRow,
  vendorNames,
) => {
  const names = (vendorNames || [])
    .map((name) => String(name || "").replace(/^ORIGINAL_/i, "").trim())
    .filter(Boolean);
  if (!names.length) {
    return;
  }

  let col = findCountryOfOriginColumn(sheet, columnKeys);
  if (col < 1) {
    col = 8;
  }

  const header = sheet.getRow(startRow).getCell(col);
  header.value = "Unique Vendors";
  header.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "d9d9d9" },
  };
  styleBorderedCell(header, { bold: true, align: "center" });

  names.forEach((name, index) => {
    const cell = sheet.getRow(startRow + 1 + index).getCell(col);
    cell.value = name;
    styleBorderedCell(cell, { align: "left" });
  });

  const column = sheet.getColumn(col);
  if (!column.width || column.width < 22) {
    column.width = 22;
  }
};

const applySummaryBlockOutline = (sheet, startRow, endRow, startCol, endCol) => {
  const left = Math.min(startCol, endCol);
  const right = Math.max(startCol, endCol);
  for (let row = startRow; row <= endRow; row += 1) {
    for (let col = left; col <= right; col += 1) {
      const cell = sheet.getCell(row, col);
      const border = {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
        ...(cell.border || {}),
      };
      if (row === startRow) {
        border.top = { style: "medium" };
      }
      if (row === endRow) {
        border.bottom = { style: "medium" };
      }
      if (col === left) {
        border.left = { style: "medium" };
      }
      if (col === right) {
        border.right = { style: "medium" };
      }
      cell.border = border;
    }
  }
};

const addQuoteSummaryBlocks = (
  sheet,
  columnKeys,
  bomlines,
  startRow,
  totals,
) => {
  const lastSumRow = getLastQuoteSumExcelRow(bomlines);
  const tariffPercentage = Number(totals.tariffPercentage);
  const safePercent = Number.isFinite(tariffPercentage) ? tariffPercentage : 0;
  const freightCost = Number(totals.freightCostPerVendor);
  const summaryRowCount = 6;

  const quoteSlots = [
    ...new Set(
      columnKeys
        .map((key) => {
          const match = key.match(/^Quote (\d+)$/);
          return match ? Number(match[1]) : null;
        })
        .filter((slot) => slot != null),
    ),
  ].sort((a, b) => a - b);

  quoteSlots.forEach((quoteSlot) => {
    const vendorCount = countUniqueQuotedVendors(bomlines, quoteSlot);
    const resolvedFreight =
      Number.isFinite(freightCost) && vendorCount > 0
        ? Math.round(freightCost * vendorCount * 100) / 100
        : 0;
    const freightLabel = Number.isFinite(freightCost)
      ? `Freight ${formatExportNumber(freightCost)}*${formatExportNumber(vendorCount)}`
      : "Freight";
    const valueCol = columnKeys.indexOf(`Quote ${quoteSlot}`) + 1;
    const labelCol = columnKeys.indexOf(`BOM Manager Result ${quoteSlot}`) + 1;
    if (valueCol < 1 || labelCol < 1) {
      return;
    }
    const valueLetter = excelColumnLetter(valueCol);
    const tariffKey = `Tariff Charge ${quoteSlot}`;
    const tariffCol = columnKeys.indexOf(tariffKey) + 1;
    const subtotalCell = `${valueLetter}${startRow}`;
    const markupCell = `${valueLetter}${startRow + 1}`;
    const freightAmountCell = `${valueLetter}${startRow + 2}`;
    const totalWithoutCell = `${valueLetter}${startRow + 3}`;
    const tariffAmountCell = `${valueLetter}${startRow + 4}`;

    const subtotalLabel = sheet.getCell(startRow, labelCol);
    subtotalLabel.value = "";
    styleBorderedCell(subtotalLabel, { bold: true });
    const subtotal = sheet.getCell(startRow, valueCol);
    subtotal.value = {
      formula: `SUM(${valueLetter}2:${valueLetter}${lastSumRow})`,
    };
    styleBorderedCell(subtotal, { bold: true, numFmt: "0.00", align: "right" });

    const markupLabel = sheet.getCell(startRow + 1, labelCol);
    markupLabel.value = `Add ${formatExportNumber(safePercent)}%`;
    styleBorderedCell(markupLabel, { bold: true });
    const markupValue = sheet.getCell(startRow + 1, valueCol);
    markupValue.value = {
      formula: `ROUND(${subtotalCell}*${safePercent}/100,2)`,
    };
    styleBorderedCell(markupValue, { numFmt: "0.00", align: "right" });

    const freightLabelCell = sheet.getCell(startRow + 2, labelCol);
    freightLabelCell.value = freightLabel;
    styleBorderedCell(freightLabelCell, { bold: true });
    const freightValue = sheet.getCell(startRow + 2, valueCol);
    freightValue.value = resolvedFreight;
    styleBorderedCell(freightValue, { numFmt: "0.00", align: "right" });

    const totalLabel = sheet.getCell(startRow + 3, labelCol);
    totalLabel.value = "Total (without tariff)";
    styleBorderedCell(totalLabel, { bold: true });
    const totalValue = sheet.getCell(startRow + 3, valueCol);
    totalValue.value = {
      formula: `ROUND(${subtotalCell}+${markupCell}+${freightAmountCell},2)`,
    };
    styleBorderedCell(totalValue, {
      bold: true,
      numFmt: "0.00",
      align: "right",
    });

    const tariffAmountLabel = sheet.getCell(startRow + 4, labelCol);
    tariffAmountLabel.value = "Tariff Amount";
    styleBorderedCell(tariffAmountLabel, { bold: true });
    const tariffAmountValue = sheet.getCell(startRow + 4, valueCol);
    if (tariffCol > 0) {
      const tariffLetter = excelColumnLetter(tariffCol);
      tariffAmountValue.value = {
        formula: `SUM(${tariffLetter}2:${tariffLetter}${lastSumRow})`,
      };
    } else {
      tariffAmountValue.value = 0;
    }
    styleBorderedCell(tariffAmountValue, {
      numFmt: '"$"#,##0.00',
      align: "right",
    });

    const totalTariffLabel = sheet.getCell(startRow + 5, labelCol);
    totalTariffLabel.value = "Total w/ Tariff";
    styleBorderedCell(totalTariffLabel, { bold: true });
    const totalTariffValue = sheet.getCell(startRow + 5, valueCol);
    totalTariffValue.value = {
      formula: `ROUND(${totalWithoutCell}+${tariffAmountCell},2)`,
    };
    styleBorderedCell(totalTariffValue, {
      bold: true,
      numFmt: "0.00",
      align: "right",
    });

    applySummaryBlockOutline(
      sheet,
      startRow,
      startRow + summaryRowCount - 1,
      labelCol,
      valueCol,
    );
  });
};

const addExportSummarySection = (sheet, columnKeys, bomlines, totals = {}) => {
  const lastDataExcelRow = (bomlines?.length || 0) + 1;
  const startRow = lastDataExcelRow + 3;
  addAssemblySpecsTable(sheet, bomlines, columnKeys, startRow);

  const hasQuoteColumns = columnKeys.some((key) => /^Quote \d+$/.test(key));
  if (hasQuoteColumns) {
    addQuoteSummaryBlocks(sheet, columnKeys, bomlines, startRow, totals);
  } else {
    addLegacyFreightTariffFooterRows(sheet, columnKeys.length, totals);
  }

  addUniqueVendorNamesBelowCountryOfOrigin(
    sheet,
    columnKeys,
    startRow,
    resolveUniqueVendorNames(bomlines, totals),
  );
};

const buildOrderedExportLine = (line, columnKeys) => {
  const ordered = {};
  (columnKeys || []).forEach((key) => {
    ordered[key] =
      line && Object.prototype.hasOwnProperty.call(line, key) ? line[key] : "";
  });
  return ordered;
};

const ExportExcel = async (
  user,
  data,
  invalidQuantityColumns,
  columns,
  exportLocally,
  bomChecklists,
  totals = {},
) => {
  const bomlines = prepareBomlinesForExport(data.bomlines, data.excelData);
  const bomId = data.bomId;
  const columnKeys = getTableColumnKeys(bomlines);
  const sourceLine =
    bomlines.find((line) => isRealBomLine(line) && !isBomSectionRow(line)) ||
    bomlines.find((line) => !isBomSectionRow(line)) ||
    bomlines[0];
  const firstBomLine = buildOrderedExportLine(sourceLine, columnKeys);

  const workbook = new ExcelJS.Workbook();

  var sheet = workbook.addWorksheet("result");
  let rowTemplate = addATemplateRowWithBasicCellFormat(sheet, firstBomLine);
  let inputColumnsCount = addSheetTitleRowAndGetInputColumnsCount(
    rowTemplate,
    firstBomLine,
    columns,
  );

  var lastBomLineIndex = bomlines?.length - 1;

  bomlines.forEach((item, rowIndex) => {
    let rowTemplate = addATemplateRowWithBasicCellFormat(sheet, firstBomLine);
    addSheetDataRow(
      rowTemplate,
      firstBomLine,
      item,
      invalidQuantityColumns,
      rowIndex,
      sheet,
      lastBomLineIndex,
      bomChecklists,
    );
  });

  addExportSummarySection(sheet, columnKeys, bomlines, totals);

  // Try the solution from the following link if required: https://github.com/exceljs/exceljs/issues/83
  // Currently, allocating more width to the result details column.
  for (let i = inputColumnsCount + 4; i <= sheet.columnCount; i++) {
    if ((i - inputColumnsCount - 1) % 3 === 2) {
      sheet.getColumn(i).width = 11;
    } else {
      sheet.getColumn(i).width = 12;
    }
  }
  sheet.getColumn(1).width = 7;
  sheet.getColumn(inputColumnsCount + 1).width = 11;
  sheet.getColumn(inputColumnsCount + 2).width = 12;
  columnKeys.forEach((key, index) => {
    if (/^BOM Manager Result \d+$/.test(key)) {
      sheet.getColumn(index + 1).width = 28;
    }
  });
  sheet.properties.defaultColWidth = 20;

  let fileNameFromLocalStorage =
    data.fileName || localStorage.getItem("fileName");
  if (data.issue_report) {
    addIssueReportWorksheet(workbook, normalizeIssueReport(data.issue_report), {
      bomName: fileNameFromLocalStorage || "BOM",
      bomlines,
    });
  }
  let fileName =
    "Result-" + (fileNameFromLocalStorage ? fileNameFromLocalStorage : "BOM");
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  await workbook.xlsx.writeBuffer().then(async (data) => {
    const blob = new Blob([data], { type: fileType });
    blob.lastModifiedDate = new Date();
    blob.name = fileName;

    // call backend to store the bom
    await storeResultantBom(user.token, {
      bomId: bomId,
      fileName: fileName,
      file: blob,
    });

    if (exportLocally) {
      FileSaver.saveAs(blob, fileName);
    }
  });
};

export default ExportExcel;
